import React, { useMemo, useState, useEffect } from 'react';
import { ChevronRight, Clock, Info } from 'lucide-react';

// ── PROTOTYPE ONLY ──────────────────────────────────────────────────────────
// Standalone, MOCKED visual of the new TIME × FORMAT grid for the WIDEST venue
// (Depot 18 football, 8 formats) on a narrow phone. No backend, no live-flow
// changes. Route: /book-grid-preview. This exists so the design can be judged
// before the full build; it will be replaced by the real data-driven grid.

const FORMATS = [
  { id: '11v11', label: '11v11', group: '11v11', from: 4000 },
  { id: '9v9-a', label: '9v9 · A', group: '9v9', from: 3000 },
  { id: '9v9-b', label: '9v9 · B', group: '9v9', from: 3000 },
  { id: '7v7-1', label: '7v7 · 1', group: '7v7', from: 2000 },
  { id: '7v7-2', label: '7v7 · 2', group: '7v7', from: 2000 },
  { id: '7v7-3', label: '7v7 · 3', group: '7v7', from: 2000 },
  { id: '7v7-4', label: '7v7 · 4', group: '7v7', from: 2000 },
  { id: '5aside', label: '5-a-side', group: '5-aside', from: 1200 },
];

const CHIPS = ['All', '11v11', '9v9', '7v7', '5-aside'];

const TIMES = ['06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00'];

// price by format group
const PRICE = { '11v11': 4000, '9v9': 3000, '7v7': 2000, '5-aside': 1200 };

// Mocked availability states to show every cell state + divisible-turf overlap.
// state: 'available' | 'booked' | 'hold' | 'overlap'
function seedState(time, fmt) {
  // A confirmed booking on 7v7-1 at 08:00 → its parent 9v9-A + 11v11 are greyed (overlap)
  if (time === '08:00') {
    if (fmt.id === '7v7-1') return 'booked';
    if (fmt.id === '9v9-a' || fmt.id === '11v11') return 'overlap';
  }
  // A live HOLD on 5-aside at 07:00 (with countdown) → nothing else overlaps 5-aside here
  if (time === '07:00' && fmt.id === '5aside') return 'hold';
  // Some scattered bookings
  if (time === '06:30' && fmt.id === '9v9-b') return 'booked';
  if (time === '09:30' && fmt.id === '11v11') return 'booked';
  return 'available';
}

export default function GridPreview() {
  const phone = typeof window !== 'undefined' && window.location.search.includes('frame=phone');
  const [chip, setChip] = useState('All');
  const [selected, setSelected] = useState(() => new Set(['09:00|7v7-2']));
  const [holdLeft, setHoldLeft] = useState(120); // 2:00 countdown on the held slot

  useEffect(() => {
    const t = setInterval(() => setHoldLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  const visibleFormats = useMemo(
    () => (chip === 'All' ? FORMATS : FORMATS.filter((f) => f.group === chip)),
    [chip]
  );

  const toggle = (time, fmt, state) => {
    if (state !== 'available') return;
    const key = `${time}|${fmt.id}`;
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  const selectedList = useMemo(
    () =>
      [...selected].map((k) => {
        const [time, fid] = k.split('|');
        const fmt = FORMATS.find((f) => f.id === fid);
        return { key: k, time, fmt, price: PRICE[fmt.group] };
      }),
    [selected]
  );
  const total = selectedList.reduce((s, x) => s + x.price, 0);
  const mmss = `${String(Math.floor(holdLeft / 60)).padStart(1, '0')}:${String(holdLeft % 60).padStart(2, '0')}`;

  return (
    <div className={phone ? 'py-6 bg-neutral-800 min-h-screen flex justify-center items-start' : ''}>
    <div className={`min-h-screen bg-[#0f1115] text-white ${phone ? 'w-[390px] max-w-[390px] rounded-[2rem] overflow-hidden shadow-2xl ring-8 ring-black relative' : ''}`} data-testid="grid-preview" style={phone ? { height: 800, minHeight: 800 } : undefined}>
      {/* Header (reuses venue/date context in real build) */}
      <div className="px-4 pt-4 pb-3 border-b border-white/10">
        <p className="text-xs text-emerald-400 font-semibold tracking-wide">DEPOT 18 · FOOTBALL</p>
        <h1 className="text-lg font-bold mt-0.5">Wed, 20 Aug · Pick your slots</h1>
        <p className="text-[11px] text-white/50 mt-1 flex items-center gap-1">
          <Info className="h-3 w-3" /> Booking a smaller format blocks the larger overlapping pitch for that time.
        </p>
      </div>

      {/* Format filter chips */}
      <div className="px-3 py-2.5 flex gap-2 overflow-x-auto no-scrollbar border-b border-white/10" data-testid="format-chips">
        {CHIPS.map((c) => (
          <button
            key={c}
            data-testid={`chip-${c}`}
            onClick={() => setChip(c)}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              chip === c ? 'bg-emerald-500 text-black' : 'bg-white/8 text-white/70 hover:bg-white/15'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* GRID: pinned time column + horizontally scrollable format columns */}
      <div className="relative">
        <div className="overflow-x-auto no-scrollbar" data-testid="grid-scroll">
          <div className="min-w-max">
            {/* Column header row */}
            <div className="flex sticky top-0 z-20 bg-[#0f1115]">
              <div className="w-16 shrink-0 sticky left-0 z-30 bg-[#0f1115] border-r border-white/10" />
              {visibleFormats.map((f) => (
                <div key={f.id} className="w-24 shrink-0 px-1.5 py-2 text-center border-b border-white/10">
                  <p className="text-[12px] font-bold leading-tight">{f.label}</p>
                  <p className="text-[10px] text-white/45">from ₹{f.from.toLocaleString('en-IN')}</p>
                </div>
              ))}
            </div>

            {/* Time rows */}
            {TIMES.map((time) => (
              <div key={time} className="flex">
                {/* Pinned time cell */}
                <div className="w-16 shrink-0 sticky left-0 z-10 bg-[#0f1115] border-r border-white/10 flex items-center justify-center">
                  <span className="text-[11px] font-semibold text-white/70">{time}</span>
                </div>
                {visibleFormats.map((f) => {
                  const state = seedState(time, f);
                  const key = `${time}|${f.id}`;
                  const isSel = selected.has(key);
                  const held = state === 'hold';
                  const base = 'w-24 shrink-0 m-0.5 rounded-lg h-14 flex flex-col items-center justify-center text-[11px] transition-all';
                  let cls, content;
                  if (isSel) {
                    cls = 'bg-emerald-500 text-black font-bold ring-2 ring-emerald-300';
                    content = <><span>₹{PRICE[f.group].toLocaleString('en-IN')}</span><span className="text-[9px]">Selected</span></>;
                  } else if (state === 'available') {
                    cls = 'bg-white/8 hover:bg-white/16 text-white cursor-pointer';
                    content = <><span className="font-semibold">₹{PRICE[f.group].toLocaleString('en-IN')}</span><span className="text-[9px] text-emerald-400">Available</span></>;
                  } else if (held) {
                    cls = 'bg-amber-500/15 text-amber-300 border border-amber-500/40 cursor-not-allowed';
                    content = <><span className="text-[9px]">On hold</span><span className="font-mono font-bold flex items-center gap-0.5"><Clock className="h-3 w-3" />{mmss}</span></>;
                  } else if (state === 'overlap') {
                    cls = 'bg-white/[0.03] text-white/25 cursor-not-allowed';
                    content = <span className="text-[9px] text-center leading-tight">Overlaps<br />booked slot</span>;
                  } else {
                    cls = 'bg-white/[0.03] text-white/30 cursor-not-allowed';
                    content = <span className="text-[9px]">Booked</span>;
                  }
                  return (
                    <button
                      key={key}
                      data-testid={`cell-${time}-${f.id}`}
                      data-state={isSel ? 'selected' : state}
                      onClick={() => toggle(time, f, state)}
                      className={`${base} ${cls}`}
                    >
                      {content}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        {/* Horizontal-scroll affordance */}
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-[#0f1115] to-transparent flex items-center justify-end pr-1">
          <ChevronRight className="h-5 w-5 text-white/40 animate-pulse" />
        </div>
      </div>

      {/* Pinned running-total summary bar */}
      {selectedList.length > 0 && (
        <div className={`${phone ? 'absolute' : 'fixed'} bottom-0 inset-x-0 bg-[#171a21] border-t border-white/10 px-4 py-3`} data-testid="summary-bar">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] text-white/50">{selectedList.length} slot{selectedList.length > 1 ? 's' : ''} selected</p>
              <p className="text-lg font-bold">₹{total.toLocaleString('en-IN')}</p>
            </div>
            <button className="bg-emerald-500 text-black font-bold px-6 py-2.5 rounded-xl text-sm" data-testid="proceed-btn">
              Proceed to pay
            </button>
          </div>
          <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
            {selectedList.map((s) => (
              <span key={s.key} className="shrink-0 text-[10px] bg-white/8 rounded-full px-2.5 py-1">
                {s.fmt.label} · {s.time} · ₹{s.price.toLocaleString('en-IN')}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
